import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PlannedAndReportedTimeService } from '@wilson/activities/reports';
import { findEarliestDepartureBasedOnDeviationTime } from '@wilson/activities/util';
import {
  Activity,
  ActivityDeviationStatus,
  ActivityReport,
} from '@wilson/interfaces';
import { ReportedTimeService } from '@wilson/non-domain-specific/shift-helpers/core';

import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { addMinutes } from 'date-fns';

@Component({
  selector: 'wilson-start-time-renderer',
  template: `
    <div class="time-tag-wrapper">
      <span class="text-dark">
        {{ cellValue ? (cellValue | localizedDateTimeFormat) : '---' }}
      </span>
      <wilson-activity-time-tag
        [activityTimeDifference]="deviatedMins"
        [activityTimeStatus]="
          reportedTime
            ? ActivityDeviationStatus.Reported
            : ActivityDeviationStatus.Deviated
        "
        [accurateTime]="reportedTime || newDeviatedHour"
      ></wilson-activity-time-tag>
    </div>
  `,
  styles: [
    `
      span {
        line-height: 10px;
      }

      .time-tag-wrapper {
        text-wrap: wrap;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StartTimeRendererComponent implements AgRendererComponent {
  private readonly reportedTimeService = inject(ReportedTimeService);
  private readonly plannedAndReportedTimeService = inject(
    PlannedAndReportedTimeService,
  );

  public cellValue!: string | null;
  public reportedTime!: string | null;
  public deviatedMins!: number | null;
  public newDeviatedHour!: string | null;
  public ActivityDeviationStatus = ActivityDeviationStatus;

  agInit(
    params: ICellRendererParams<{ activities: Activity[] }, string>,
  ): void {
    this.init(params);
  }

  refresh(
    params: ICellRendererParams<{ activities: Activity[] }, string>,
  ): boolean {
    this.init(params);
    return true;
  }

  private init(
    params: ICellRendererParams<{ activities: Activity[] }, string>,
  ): void {
    this.cellValue = params.value ?? null;
    this.reportedTime = this.getReportedStartTime(params);

    const departureDeviationRecord = findEarliestDepartureBasedOnDeviationTime(
      params.data?.activities ?? null,
    );

    if (departureDeviationRecord?.departureDeviation) {
      this.cellValue = departureDeviationRecord.startDatetime;
      this.deviatedMins =
        departureDeviationRecord.departureDeviation.deviationInMinutes;
      this.newDeviatedHour = this.deviatedMins
        ? addMinutes(new Date(this.cellValue), this.deviatedMins).toString()
        : null;
    }
  }

  getReportedStartTime(
    params: ICellRendererParams<{ activities: Activity[] }>,
  ): string | null {
    const activities = params?.data?.activities;
    const activityReports = this.firstReportedActivityReports(activities);
    const reportedStartTime =
      this.plannedAndReportedTimeService.reportedStartTime(activityReports);
    return reportedStartTime;
  }

  private firstReportedActivityReports(
    activities: Activity[] | undefined,
  ): ActivityReport[] {
    if (activities) {
      activities = this.reportedTimeService.reportedActivities(activities);
      const firstActivity = activities[0];
      return firstActivity?.activityReports ?? [];
    } else {
      return [];
    }
  }
}
